/* eslint-disable */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaTasks,
  FaHourglassHalf,
  FaSpinner,
  FaCheckCircle,
  FaClipboardCheck,
  FaClock,
} from "react-icons/fa"; // Importing icons
import "../style/dashboard.css"; // Import CSS for styling
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// Premium Vibrant Status Colors: Pending (Red), Ongoing (Orange), InReview (Blue), Completed (Green), Deadline (Crimson)
const COLORS = ["#ff4d4d", "#ffa500", "#3b82f6", "#2ecc71", "#c0392b"];
const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalTasks: 0,
    pending: 0,
    ongoing: 0,
    inReview: 0,
    completed: 0,
    deadline: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userData = JSON.parse(localStorage.getItem("user"));
  const userId = userData?.id;

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await axios.get(
          `/api/dashboard/${userId}`
        );

        if (response.status === 200 && response.data) {
          console.log("Data:", response.data); // Debugging API response

          const {
            task_status_counts = {},
            total_tasks = 0,
            deadline_count = 0,
          } = response.data;
          // Ensure that no error is set when response is valid
          setError(null);

          setDashboardData({
            totalTasks: total_tasks || 0,
            pending: task_status_counts.pending || 0,
            ongoing: task_status_counts.ongoing || 0,
            inReview: task_status_counts.inreview || 0,
            completed: task_status_counts.completed || 0,
            deadline: deadline_count || 0, // Adding Deadline Count
          });
        } else {
          setError("Invalid data received from API");
        }
      } catch (err) {
        console.error("API Error:", err);
        // setError("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userId]);

  const pieData = [
    { name: "Pending", value: dashboardData.pending },
    { name: "Ongoing", value: dashboardData.ongoing },
    { name: "InReview", value: dashboardData.inReview },
    { name: "Completed", value: dashboardData.completed },
    { name: "Deadline", value: dashboardData.deadline },
  ];

  if (loading) return <p>Loading...</p>;
  //Only show the error message if there's an actual API failure, not when tasks are 0
  if (error) return <p className="error">{error}</p>;
  const noTasks = Object.values(dashboardData).every((count) => count === 0);
  return (
    <div className="dashboard-main">
      <div className="dashboard">
        {/* {noTasks && <p className="no-tasks">No tasks assigned yet.</p>} */}
        <div className="card total">
          <div className="icon-bg">
            <FaTasks />
          </div>
          <div className="card-text">
            <h3>Total Tasks</h3>
            <p>{dashboardData.totalTasks}</p>
          </div>
        </div>
        <div className="card pending">
          <div className="icon-bg">
            <FaHourglassHalf />
          </div>
          <div className="card-text">
            <h3>Pending</h3>
            <p>{dashboardData.pending}</p>
          </div>
        </div>
        <div className="card ongoing">
          <div className="icon-bg">
            <FaSpinner />
          </div>
          <div className="card-text">
            <h3>Ongoing</h3>
            <p>{dashboardData.ongoing}</p>
          </div>
        </div>
        <div className="card in-review">
          <div className="icon-bg">
            <FaClipboardCheck />
          </div>
          <div className="card-text">
            <h3>InReview</h3>
            <p>{dashboardData.inReview}</p>
          </div>
        </div>
        <div className="card completed">
          <div className="icon-bg">
            <FaCheckCircle />
          </div>
          <div className="card-text">
            <h3>Completed</h3>
            <p>{dashboardData.completed}</p>
          </div>
        </div>
        <div className="card deadline">
          <div className="icon-bg">
            <FaClock />
          </div>
          <div className="card-text">
            <h3>Deadline</h3>
            <p>{dashboardData.deadline}</p>
          </div>
        </div>
      </div>
      <div className="chart-main-container">
        <div className="chart-container">
          <h3>Task Status Overview</h3>
          <ResponsiveContainer width="100%" height={380}>
            <PieChart>
              <Pie
                data={dashboardData.totalTasks === 0 ? [{ value: 1 }] : pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                innerRadius={82}
                outerRadius={105}
                paddingAngle={0}
                dataKey="value"
                stroke="var(--bg-card)"
                strokeWidth={4}
                label={dashboardData.totalTasks === 0 ? false : ({ cx, cy, midAngle, innerRadius, outerRadius, value, index, name, percent }) => {
                  if (value === 0) return null;
                  const radius = outerRadius + 25;
                  const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                  const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

                  // Custom label line
                  const lineX1 = cx + outerRadius * Math.cos(-midAngle * (Math.PI / 180));
                  const lineY1 = cy + outerRadius * Math.sin(-midAngle * (Math.PI / 180));
                  const lineX2 = cx + (outerRadius + 20) * Math.cos(-midAngle * (Math.PI / 180));
                  const lineY2 = cy + (outerRadius + 20) * Math.sin(-midAngle * (Math.PI / 180));

                  return (
                    <g>
                      <line x1={lineX1} y1={lineY1} x2={lineX2} y2={lineY2} stroke="#94a3b8" strokeWidth={1} />
                      <text x={x} y={y - 8} fill="#ffffff" textAnchor={x > cx ? "start" : "end"} style={{ fontSize: "13px", fontWeight: "600" }}>
                        {`${(percent * 100).toFixed(0)}%`}
                      </text>
                      <text x={x} y={y + 10} fill="#94a3b8" textAnchor={x > cx ? "start" : "end"} style={{ fontSize: "10px", fontWeight: "500", textTransform: 'uppercase' }}>
                        {`${name} (${value})`}
                      </text>
                    </g>
                  );
                }}
              >
                {dashboardData.totalTasks === 0 ? (
                  <Cell fill="rgba(255, 255, 255, 0.05)" />
                ) : (
                  pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))
                )}
              </Pie>
              <text x="50%" y="40%" textAnchor="middle" dominantBaseline="middle">
                <tspan x="50%" dy="0" fill="#94a3b8" style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                  TOTAL TASKS
                </tspan>
                <tspan x="50%" dy="35" fill="#ffffff" style={{ fontSize: '38px', fontWeight: '800' }}>
                  {dashboardData.totalTasks}
                </tspan>
              </text>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: 'var(--border)',
                  borderRadius: '10px',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
                itemStyle={{ color: '#ffffff', fontSize: '14px', fontWeight: '500' }}
                labelStyle={{ color: '#cbd5e1', marginBottom: '4px' }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                formatter={(value) => <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '500' }}>{value}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
export default Dashboard;
